import { EmptyState } from "@/components/empty-state";
import { PageHeader } from "@/components/page-header";
import { TableSkeleton } from "@/components/skeleton";
import { type DataTableColumn, DataTableV2 } from "@/components/ui/data-table-v2";
import { ErrorState } from "@/components/ui/error-state";
import { Input } from "@/components/ui/input";
import { StatusBadge } from "@/components/ui/status-badge";
import { useCurrentProject } from "@/features/projects/current-project";
import { useNavigate } from "@tanstack/react-router";
import type { ColumnDef } from "@tanstack/react-table";
import { useMemo, useState } from "react";
import { type JobOut, type JobStatus, durationMs, useJobs } from "./queries";

function formatMs(ms: number | null): string {
  if (!ms) return "—";
  const s = Math.floor(ms / 1000);
  const m = Math.floor(s / 60);
  return `${String(m).padStart(2, "0")}:${String(s % 60).padStart(2, "0")}`;
}

const columns: (ColumnDef<JobOut, unknown> & DataTableColumn)[] = [
  {
    header: "ID",
    accessorKey: "id",
    mono: true,
    cell: ({ row }) => <span className="mono text-fg-muted">{row.original.id.slice(0, 8)}…</span>,
  },
  { header: "Kind", accessorKey: "kind" },
  {
    header: "Status",
    cell: ({ row }) => <StatusBadge status={row.original.status} />,
  },
  {
    header: "Duration",
    cell: ({ row }) => (
      <span className="mono text-fg-muted">{formatMs(durationMs(row.original))}</span>
    ),
  },
  {
    header: "Queued at",
    cell: ({ row }) => (
      <span className="mono text-fg-muted">
        {new Date(row.original.queued_at).toLocaleString()}
      </span>
    ),
  },
];

const STATUS_OPTS: JobStatus[] = ["queued", "running", "done", "failed", "cancelled"];

export function JobsListPage() {
  const { projectId } = useCurrentProject();
  const [page, setPage] = useState(1);
  const pageSize = 20;
  const { data, isLoading, isError, refetch } = useJobs(projectId, { page, pageSize });
  const navigate = useNavigate();
  const [kind, setKind] = useState<string>("");
  const [status, setStatus] = useState<string>("");
  const [from, setFrom] = useState<string>("");
  const [to, setTo] = useState<string>("");

  const kinds = useMemo(() => {
    const s = new Set<string>();
    for (const j of data?.items ?? []) s.add(j.kind);
    return [...s].sort();
  }, [data?.items]);

  const filtered = useMemo(() => {
    const items = data?.items ?? [];
    const fromTs = from ? new Date(from).getTime() : null;
    const toTs = to ? new Date(`${to}T23:59:59`).getTime() : null;
    return items
      .filter((j) => (kind ? j.kind === kind : true))
      .filter((j) => (status ? j.status === status : true))
      .filter((j) => {
        const t = new Date(j.queued_at).getTime();
        if (fromTs != null && t < fromTs) return false;
        if (toTs != null && t > toTs) return false;
        return true;
      })
      .slice()
      .sort((a, b) => b.queued_at.localeCompare(a.queued_at));
  }, [data?.items, kind, status, from, to]);

  const clearFilters = () => {
    setKind("");
    setStatus("");
    setFrom("");
    setTo("");
  };

  if (!projectId) {
    return (
      <>
        <PageHeader title="Jobs" subtitle="Async tasks · auto-refresh while running" />
        <EmptyState title="No project selected" description="Choose a project to view its jobs." />
      </>
    );
  }

  return (
    <>
      <PageHeader title="Jobs" subtitle="Async tasks · auto-refresh while running" />
      <div className="mb-3 flex flex-wrap items-end gap-3">
        <label className="flex flex-col gap-1">
          <span className="text-[10px] font-medium uppercase tracking-wider text-fg-muted">
            Kind
          </span>
          <select
            value={kind}
            onChange={(e) => setKind(e.target.value)}
            className="rounded border border-border-strong bg-surface-2 px-2 py-1.5 text-xs"
          >
            <option value="">All</option>
            {kinds.map((k) => (
              <option key={k} value={k}>
                {k}
              </option>
            ))}
          </select>
        </label>
        <label className="flex flex-col gap-1">
          <span className="text-[10px] font-medium uppercase tracking-wider text-fg-muted">
            Status
          </span>
          <select
            value={status}
            onChange={(e) => setStatus(e.target.value)}
            className="rounded border border-border-strong bg-surface-2 px-2 py-1.5 text-xs"
          >
            <option value="">All</option>
            {STATUS_OPTS.map((s) => (
              <option key={s} value={s}>
                {s}
              </option>
            ))}
          </select>
        </label>
        {/* biome-ignore lint/a11y/noLabelWithoutControl: input is control */}
        <label className="flex flex-col gap-1">
          <span className="text-[10px] font-medium uppercase tracking-wider text-fg-muted">
            From
          </span>
          <Input type="date" value={from} onChange={(e) => setFrom(e.target.value)} />
        </label>
        {/* biome-ignore lint/a11y/noLabelWithoutControl: input is control */}
        <label className="flex flex-col gap-1">
          <span className="text-[10px] font-medium uppercase tracking-wider text-fg-muted">To</span>
          <Input type="date" value={to} onChange={(e) => setTo(e.target.value)} />
        </label>
        {(kind || status || from || to) && (
          <button
            type="button"
            onClick={clearFilters}
            className="text-xs text-fg-muted hover:text-fg underline"
          >
            Clear
          </button>
        )}
        <div className="ml-auto text-xs text-fg-muted">
          {filtered.length} / {data?.total ?? 0} jobs
        </div>
      </div>
      {isLoading ? (
        <TableSkeleton />
      ) : isError ? (
        <ErrorState message="Failed to load jobs" onRetry={() => refetch()} />
      ) : (
        <DataTableV2
          // Backend /v1/jobs does not paginate today; slice the filtered list
          // client-side so the footer "Showing X–Y of N" matches rendered rows.
          data={
            filtered.length > pageSize
              ? filtered.slice((page - 1) * pageSize, page * pageSize)
              : filtered
          }
          columns={columns}
          caption="All jobs"
          page={page}
          pageSize={pageSize}
          total={filtered.length}
          onPageChange={setPage}
          onRowClick={(j) => navigate({ to: "/jobs/$jobId", params: { jobId: j.id } })}
        />
      )}
    </>
  );
}
